import { NaiveDate } from "@tento-chrono";
import type { Option } from "../../core/option.ts";
import type { RowLocalId } from "../../ids/row-local-id.ts";

/** A null range preserves the existing whole-row invalidation contract. */
export type RowContentChange = {
  rowId: RowLocalId;
  range: Option<NaiveDate.Range>;
};

/**
 * The date ranges of a row whose content changed, or `null` for the whole
 * row.
 */
export type RowContentRanges = Option<readonly NaiveDate.Range[]>;

/** Merge touching intervals per row, retaining gaps and whole-row dominance. */
export function collectRowContentRanges(
  changes: Iterable<RowContentChange>,
): Map<RowLocalId, RowContentRanges> {
  const rows = new Map<RowLocalId, Option<NaiveDate.Range[]>>();
  for (const { rowId, range } of changes) {
    const previous = rows.get(rowId);
    if (rows.has(rowId) && previous == null) continue;
    if (range == null) rows.set(rowId, null);
    else if (previous == null) rows.set(rowId, [range]);
    else previous.push(range);
  }
  for (const [rowId, ranges] of rows) {
    if (ranges == null) continue;
    ranges.sort((a, b) => a.start.dse - b.start.dse);
    const merged: NaiveDate.Range[] = [];
    for (const range of ranges) {
      const last = merged.at(-1);
      if (last == null || range.start.dse > last.end.dse) merged.push(range);
      else if (range.end.dse > last.end.dse) {
        merged[merged.length - 1] = new NaiveDate.Range(last.start, range.end);
      }
    }
    rows.set(rowId, merged);
  }
  return rows;
}

export type RowContentColumnCoverage = {
  local: { start: number; end: number };
  /** Fractional UTC days preserve partial flight/timezone column bounds. */
  utc: { start: number; end: number };
};

/** Unknown custom-column coverage remains eligible for a content refresh. */
export function rowContentIntersects(
  column: Option<RowContentColumnCoverage>,
  ranges: RowContentRanges,
): boolean {
  if (ranges == null) return true;
  if (ranges.length === 0) return false;
  if (column == null) return true;
  return ranges.some(({ start, end }) =>
    (column.local.start < end.dse && column.local.end > start.dse) ||
    // Bulk fetch listeners carry UTC endpoints rounded down to dates. The
    // end may have been inside this UTC day; keep it conservatively eligible.
    // Testing UTC coverage also retains local days across timezone boundaries.
    (column.utc.start < end.dse + 1 && column.utc.end > start.dse)
  );
}
